/**
 * Google reCAPTCHA v3 - lazy script loader + token generator.
 *
 * Fail-open by design: every failure path resolves to `null` instead of
 * throwing, so a Google outage (or an adblocker) can never lock a real user
 * out of login. Enforcement lives entirely on the backend, which verifies the
 * token via `siteverify` and decides what to do when the header is missing.
 */

declare global {
  interface Window {
    grecaptcha?: {
      ready: (callback: () => void) => void;
      execute: (
        siteKey: string,
        options: { action: string },
      ) => Promise<string>;
    };
  }
}

export const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY || "";

/** Header the backend reads the token from. Agreed contract - do not rename alone. */
export const RECAPTCHA_HEADER = "x-recaptcha-token";

/**
 * The two actions the backend asserts on. Agreed contract - adding a third
 * means telling the backend about it first, or the token is rejected.
 */
export const RECAPTCHA_ACTION_REQUEST_LOGIN = "request_login";
export const RECAPTCHA_ACTION_PASSWORD_RESET = "password_reset";

/**
 * Unauthenticated OTP-triggering mutations, mapped to their action. Keys are
 * GraphQL *operation* names as written in `src/apollo/mutations/*.gql.ts`,
 * which do not always match the underlying field (e.g. the operation
 * `requestChangePassword` calls the field `requestChangePasswordV2`).
 *
 * Mutations behind a valid access token are deliberately left out: the token
 * already identifies the caller, so reCAPTCHA adds nothing there.
 *
 * Anything absent from this map is sent without a token, so a new anonymous
 * OTP flow ships unprotected unless it is added here.
 */
export const RECAPTCHA_ACTION_BY_OPERATION: Record<string, string> = {
  // Login screen, "gửi lại" on the OTP screen, and the data-consent page.
  requestLogin: RECAPTCHA_ACTION_REQUEST_LOGIN,
  // Public /forgot-password flow.
  requestChangePassword: RECAPTCHA_ACTION_PASSWORD_RESET,
  // Change-password OTP from PinVerification (also used by SettingPage).
  requestChangePasswordWithoutSocialId: RECAPTCHA_ACTION_PASSWORD_RESET,
};

const SCRIPT_ID = "recaptcha-v3";
const SCRIPT_SRC = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;

/** Bound wait so a hanging Google request can never hang the login button. */
const EXECUTE_TIMEOUT_MS = 5_000;

let loadPromise: Promise<boolean> | null = null;

const withTimeout = <T>(promise: Promise<T>, ms: number): Promise<T | null> =>
  Promise.race([
    promise,
    new Promise<null>((resolve) => setTimeout(() => resolve(null), ms)),
  ]);

/**
 * Injects the reCAPTCHA script once and resolves when `grecaptcha` is ready.
 * Safe to call repeatedly - later calls reuse the first promise.
 */
export const loadRecaptcha = (): Promise<boolean> => {
  if (!RECAPTCHA_SITE_KEY) return Promise.resolve(false);
  if (loadPromise) return loadPromise;

  loadPromise = new Promise<boolean>((resolve) => {
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      resolve(Boolean(window.grecaptcha));
      return;
    }

    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.async = true;
    script.onload = () => {
      if (!window.grecaptcha) {
        resolve(false);
        return;
      }
      window.grecaptcha.ready(() => resolve(true));
    };
    script.onerror = () => {
      console.warn("[recaptcha] script failed to load");
      resolve(false);
    };

    document.head.appendChild(script);
  }).then((ready) => {
    // Let a failed load be retried on the next attempt instead of caching it.
    if (!ready) loadPromise = null;
    return ready;
  });

  return loadPromise;
};

/**
 * Returns a fresh reCAPTCHA token for `action`, or `null` when reCAPTCHA is
 * disabled/unavailable. Tokens expire after 120s, so call this at send time.
 */
export const executeRecaptcha = async (
  action: string,
): Promise<string | null> => {
  if (!RECAPTCHA_SITE_KEY) return null;

  try {
    const ready = await withTimeout(loadRecaptcha(), EXECUTE_TIMEOUT_MS);
    if (!ready || !window.grecaptcha) return null;

    const token = await withTimeout(
      window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action }),
      EXECUTE_TIMEOUT_MS,
    );

    return token || null;
  } catch (error) {
    console.warn("[recaptcha] execute failed", error);
    return null;
  }
};

